import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { format } from "date-fns";

import { getActiveCompanyHolidayListForEmployeeQueryOptions } from "@/config/queries/emp-query-options";
import { Spinner } from "@/components/ui/spinner/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import { Badge } from "@/components/ui/badge/badge";

const EmployeeCompanyHolidaysPage = () => {
  const employeeKey = Cookies.get("key") as string;
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const { data, isPending } = useQuery(
    getActiveCompanyHolidayListForEmployeeQueryOptions(employeeKey, selectedYear)
  );

  const sortedHolidays = useMemo(() => {
    if (!data?.foundList?.holidays) {
      return [];
    }

    return [...data.foundList.holidays].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [data]);

  const years = useMemo(() => {
    const result = [];
    for (let year = currentYear - 2; year <= currentYear + 1; year++) {
      result.push(year);
    }
    return result;
  }, [currentYear]);

  const isUpcoming = (dateStr: string | Date) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  };

  return (
    <div className="p-5 h-[calc(100%-56px)]">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-medium">Company Holidays</h3>

        <Select
          value={selectedYear.toString()}
          onValueChange={(value) => setSelectedYear(parseInt(value))}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent>
            {years.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5">
        {isPending ? (
          <div className="h-20 flex items-center justify-center">
            <Spinner size="small">
              <p>Loading holidays...</p>
            </Spinner>
          </div>
        ) : !data?.foundList ? (
          <div className="border h-20 flex items-center justify-center text-muted-foreground">
            No company holiday list found for {selectedYear}.
          </div>
        ) : sortedHolidays.length === 0 ? (
          <div className="border h-20 flex items-center justify-center text-muted-foreground">
            No holidays in the list for {selectedYear}.
          </div>
        ) : (
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">#</TableHead>
                  <TableHead>Holiday</TableHead>
                  <TableHead className="w-[150px]">Date</TableHead>
                  <TableHead className="w-[120px]">Day</TableHead>
                  <TableHead className="w-[100px]">Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedHolidays.map((holiday, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell className="font-medium">
                      {holiday.holidayName}
                    </TableCell>
                    <TableCell>
                      {format(new Date(holiday.date), "PPP")}
                    </TableCell>
                    <TableCell>
                      {format(new Date(holiday.date), "EEEE")}
                    </TableCell>
                    <TableCell>
                      {holiday.isHalfDay ? (
                        <Badge variant="outline" className="text-orange-600 border-orange-600">
                          Half Day
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-green-600 border-green-600">
                          Holiday
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {holiday.description || "-"}
                    </TableCell>
                    <TableCell>
                      {isUpcoming(holiday.date) ? (
                        <Badge variant="default">Upcoming</Badge>
                      ) : (
                        <Badge variant="secondary">Past</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {data?.foundList && (
        <div className="mt-4 space-y-2">
          <div className="text-sm text-muted-foreground">
            Showing {sortedHolidays.length} holidays for {selectedYear}
            {sortedHolidays.some(h => h.isHalfDay) && (
              <span>
                {" "}({sortedHolidays.filter(h => !h.isHalfDay).length} full holidays, {sortedHolidays.filter(h => h.isHalfDay).length} half-days)
              </span>
            )}
          </div>
          {sortedHolidays.some(h => h.isHalfDay) && (
            <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded-md">
              Note: On half-days, you are expected to work reduced hours. Check-in and check-out will be processed normally.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmployeeCompanyHolidaysPage;
